// The overlay layer: Dialog, ConfirmDialog and usePopover (UI-REMEDIATION-PLAN
// §4.3, theme T3). Before this there were eight hand-built modals with three
// scrim recipes, no focus trap, no focus return, three that ignored Escape and
// one that was not a dialog at all (DS-05, XA-05), plus three private popovers
// (Select, OrgTime, NotificationBell) with a different subset of Escape, blur
// and aria each (XA-V1).
//
// Everything here stands on the platform rather than on a portal and a z-index:
// <dialog>.showModal() puts the panel in the top layer, makes the page behind it
// inert (that IS the focus trap), and reports close requests as `cancel`
// (Escape is taken earlier, at the key; see OpenDialog); a
// `popover="manual"` panel is top layer too. No portal, so a popover rendered
// next to its trigger inside a modal stays inside it — a panel portalled to
// <body> would be inert behind the dialog.
//
// primitives.tsx imports usePopover from here and this file imports Button and
// Icon from there. Neither touches the other at module load, only at render,
// so the cycle is safe.
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { Button, Icon } from './primitives';
import { Surface } from './card';
import { useBelow } from '../lib/useMediaQuery';

const FOCUSABLE =
  'button:not(:disabled), [href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';
const FIELD =
  'input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled)';

// The element itself when it can take focus, else the first thing inside it that
// can. Lets initialFocus point at a wrapper around a primitive that takes no ref.
const focusIn = (el: Element | null | undefined): boolean => {
  const target = el?.matches(FOCUSABLE) ? el : el?.querySelector(FOCUSABLE);
  if (!(target instanceof HTMLElement)) return false;
  target.focus();
  return true;
};

const WIDTH = { sm: 460, md: 520, xl: 1040, lg: 1100 } as const;

// Where each open dialog hands focus back on close, nearest first. A dialog
// opened from a button inside another inherits that one's list: calendar's
// EventDetail "Edit" closes EventDetail and opens EventForm in one commit, so
// by the time EventForm closes its opener is gone, and focus would drop to
// <body> (XA-05) instead of landing on what opened EventDetail.
const returnTo = new WeakMap<Element, HTMLElement[]>();

/** Content, or a function given `requestClose`: the guarded close Escape, the ×
 *  and the backdrop use, so a Cancel button asks "Discard changes?" too. A
 *  Cancel wired straight to onClose skips that question. */
type Slot = ReactNode | ((requestClose: () => void) => ReactNode);

type DialogProps = {
  open: boolean;
  /** A close request (Escape, the ×, the backdrop). Unmount or set open=false. */
  onClose: () => void;
  /** Rendered as the dialog's h2, and its accessible name. */
  title: string;
  size?: keyof typeof WIDTH;
  /** Actions row: ghost Cancel to the left of the primary action. */
  footer?: Slot;
  /** Unsaved input. A close request asks "Discard changes?" instead of closing. */
  dirty?: boolean;
  /** Focused on open, or the first focusable inside it. Default: the first field,
   *  else the × button. React's `autoFocus` does NOT work in here: React calls
   *  focus() while committing, before the dialog is shown, and a closed dialog's
   *  content can't take focus. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** 'start': a full-height drawer from the left edge (the phone's nav), not a
   *  centred panel. `size` doesn't apply. */
  placement?: 'center' | 'start';
  /** No padding, title row or footer: the children draw their own header and
   *  call requestClose (the work item view). `title` still names the dialog. */
  bare?: boolean;
  children?: Slot;
};

// Rendered only while open, so every opening starts from fresh state and the
// opener is read at the moment it opens.
export function Dialog(props: DialogProps) {
  return props.open ? <OpenDialog {...props} /> : null;
}

function OpenDialog({
  onClose,
  title,
  size = 'md',
  footer,
  dirty = false,
  initialFocus,
  placement = 'center',
  bare = false,
  children,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  // Below sm (480) a centred panel is a phone-width box with a gutter, its
  // actions wherever the form ends. It becomes a bottom sheet instead: full
  // width, rising from where the thumb is, the title and actions fixed and only
  // the body scrolling, so Save is never scrolled away (UI-REMEDIATION-PLAN §6
  // Phase 4). The drawer has the same frame. dvh, not vh: the bar a phone's
  // browser shows or hides doesn't push the sheet's foot off the screen.
  const sheet = useBelow('sm') && placement === 'center';
  const framed = sheet || placement === 'start';
  const titleId = useId();
  const discardId = useId();
  // Whatever had focus when this opened, so closing hands it back instead of
  // dropping focus on <body> (XA-05: after Escape the bug report left focus on
  // BODY). Read during the first render, as timer.tsx does: an effect runs after
  // a child's autoFocus may already have moved it. Also read then: the dialog
  // the opener sits in is still open, even if this same commit closes it.
  const [openers] = useState(() => {
    const a = document.activeElement as HTMLElement | null;
    const outer = a?.closest('dialog');
    return [a, ...((outer && returnTo.get(outer)) ?? [])].filter((o) => o !== null);
  });
  // Fixed at open, so a caller passing a fresh ref object each render cannot
  // re-run the open effect (close, reopen, refocus) on every keystroke.
  const [focusTarget] = useState(() => initialFocus);
  const [discarding, setDiscarding] = useState(false);
  const keepRef = useRef<HTMLSpanElement>(null);
  const resumeRef = useRef<HTMLElement | null>(null);
  const pressedBackdrop = useRef(false);
  const closedByUs = useRef(false);

  useLayoutEffect(() => {
    const d = ref.current;
    if (!d) return;
    closedByUs.current = false;
    returnTo.set(d, openers);
    if (!d.open) d.showModal();
    if (!focusIn(focusTarget?.current) && !focusIn(d.querySelector(FIELD))) focusIn(d);
    return () => {
      // close() first: while the dialog is modal the opener is inert and
      // focus() on it does nothing.
      closedByUs.current = true;
      returnTo.delete(d);
      if (d.open) d.close();
      openers.find((o) => o.isConnected)?.focus();
    };
  }, [focusTarget, openers]);

  useEffect(() => {
    if (discarding) focusIn(keepRef.current);
  }, [discarding]);

  // The on-screen keyboard. Chromium shrinks the layout viewport for it
  // (interactive-widget=resizes-content, index.html), so dvh and the sheet's
  // foot follow. WebKit shrinks only the VISUAL viewport, and a sheet standing
  // on the layout one kept its actions under the keyboard. --hf-kb is the strip
  // of layout viewport the visual one no longer shows at the bottom: the sheet
  // stands on it. 0 where the two agree (Android, no keyboard) and under a
  // pinch zoom, which must not move the sheet.
  useEffect(() => {
    const vv = window.visualViewport;
    const d = ref.current;
    if (!sheet || !vv || !d) return;
    const fit = () => {
      const lost = vv.scale > 1 ? 0 : window.innerHeight - vv.height - vv.offsetTop;
      d.style.setProperty('--hf-kb', `${Math.max(0, Math.round(lost))}px`);
    };
    fit();
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    return () => {
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
    };
  }, [sheet]);

  // Where "Keep editing" puts you back: what had focus, if it was in here.
  // Focus can sit on <body> in a modal (its element was removed), and that
  // is no place to return to; the dialog's first control is.
  const holdPlace = () => {
    const a = document.activeElement;
    resumeRef.current = a instanceof HTMLElement && ref.current?.contains(a) ? a : null;
  };
  const requestClose = () => {
    if (!dirty) return onClose();
    if (!discarding) holdPlace();
    setDiscarding(true);
  };
  const keepEditing = () => {
    setDiscarding(false);
    if (!focusIn(resumeRef.current)) focusIn(ref.current);
  };
  // Escape, or a back gesture: while "Discard changes?" is up it means "no,
  // take me back", never a second step towards discarding.
  const back = () => (discarding ? keepEditing() : requestClose());

  // A stray click must not throw away a form (the bug report was lost to one,
  // BugReportModal): only a press that starts AND ends on the backdrop counts,
  // so a text selection dragged out of a field is not a close. Clicks on the
  // ::backdrop are dispatched to the <dialog>, and so are clicks on its own
  // scrollbar, hence the rect test rather than target alone.
  const onBackdrop = (e: MouseEvent<HTMLDialogElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return (
      e.target === e.currentTarget &&
      (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom)
    );
  };

  return (
    <dialog
      ref={ref}
      // The classes carry only the entry movement (tokens.css); the box is below.
      className={`hf-dialog${sheet ? ' hf-sheet' : placement === 'start' ? ' hf-drawer' : ''}`}
      aria-labelledby={bare ? undefined : titleId}
      aria-label={bare ? title : undefined}
      // Escape is taken at the key, not at `cancel`: Chrome lets only one cancel
      // per user activation be prevented, so a second Escape (the natural way
      // out of the discard prompt) came uncancelable and shut a dirty dialog
      // with no Discard pressed. A prevented keydown never becomes a close
      // request at all. A widget inside that owns Escape (Select) prevents or
      // stops it first, and then it is not ours (DS-V1); nor mid-IME. Ours
      // stops here too, as usePopover's does: Escape closes the innermost
      // widget and stops there (UI-CONVENTIONS §4). React bubbles the key up
      // its own tree, so a usePopover panel the dialog was opened from closed
      // on the same press.
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || e.defaultPrevented || e.nativeEvent.isComposing) return;
        e.preventDefault();
        e.stopPropagation();
        back();
      }}
      // Close requests that are not a key (Android back; Escape while focus is
      // on <body>) still come as `cancel`. Prevented, so the dialog stays
      // controlled: the parent decides by unmounting.
      onCancel={(e) => {
        if (e.cancelable) {
          e.preventDefault();
          back();
        } else if (!discarding) {
          holdPlace();
        }
      }}
      // `close` is queued, not fired in place: under StrictMode's mount, unmount,
      // mount, the first unmount's close lands on the reopened dialog. Only a
      // dialog that is actually shut was closed by the platform: an uncancelable
      // cancel. Unsaved input still gets asked about, so reopen and ask.
      onClose={() => {
        const d = ref.current;
        if (closedByUs.current || !d || d.open) return;
        if (!dirty) return onClose();
        d.showModal();
        if (discarding) focusIn(keepRef.current);
        else setDiscarding(true);
      }}
      onMouseDown={(e) => {
        pressedBackdrop.current = onBackdrop(e);
      }}
      onClick={(e) => {
        if (pressedBackdrop.current && onBackdrop(e)) requestClose();
        pressedBackdrop.current = false;
      }}
      style={{
        padding: 0,
        color: 'var(--hf-ink)',
        ...(sheet
          ? {
              width: '100%',
              maxWidth: '100%',
              // --hf-kb: above an iOS keyboard (the effect above).
              maxHeight: 'calc(90dvh - var(--hf-kb))',
              margin: 'auto 0 var(--hf-kb)',
              borderBottom: 0,
              borderRadius: 'var(--hf-radius-card) var(--hf-radius-card) 0 0',
              overflow: 'hidden',
            }
          : placement === 'start'
            ? {
                width: 'min(20rem, calc(100% - var(--hf-space-7)))',
                maxWidth: 'none',
                height: '100dvh',
                maxHeight: 'none',
                margin: '0 auto 0 0',
                borderLeft: 0,
                borderRadius: '0 var(--hf-radius-card) var(--hf-radius-card) 0',
                overflow: 'hidden',
              }
            : {
                width: WIDTH[size],
                // Fits a tablet with a gutter; the UA max-height and overflow:auto
                // keep a tall form scrollable inside the top layer.
                maxWidth: 'calc(100% - 2 * var(--hf-space-4))',
                borderRadius: 'var(--hf-radius-card)',
              }),
      }}
    >
      <div
        style={
          bare
            ? { display: 'flex', flexDirection: 'column', maxHeight: 'inherit' }
            : framed
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  // The dialog's own cap, so the body below is what gives.
                  maxHeight: 'inherit',
                  height: sheet ? undefined : '100%',
                  // Clear of a phone's notch (landscape) and home indicator.
                  padding: [
                    'max(var(--hf-space-4), env(safe-area-inset-top))',
                    'max(var(--hf-space-4), env(safe-area-inset-right))',
                    'max(var(--hf-space-4), env(safe-area-inset-bottom))',
                    'max(var(--hf-space-4), env(safe-area-inset-left))',
                  ].join(' '),
                }
              : { padding: 'var(--hf-space-5)' }
        }
      >
        {!bare && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--hf-space-3)',
              marginBottom: 'var(--hf-space-4)',
              flexShrink: 0,
            }}
          >
            <h2
              id={titleId}
              style={{
                flex: 1,
                minWidth: 0,
                margin: 0,
                fontSize: 'var(--hf-text-lg)',
                fontWeight: 'var(--hf-weight-medium)',
                color: 'var(--hf-ink)',
                overflowWrap: 'anywhere',
              }}
            >
              {title}
            </h2>
            {/* A 32px target where the bare 16px glyph always sat: the padding
              grows the box, the negative margin takes it back out of the row
              (WCAG 2.5.8), less the UA's 1px 6px button padding it replaced,
              so the glyph and the title's width are where they were. .hf-hit
              makes it 44 under a finger. */}
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              className="hf-hit"
              style={{
                background: 'transparent',
                border: 0,
                padding: 'var(--hf-space-2)',
                margin: 'calc(1px - var(--hf-space-2)) calc(6px - var(--hf-space-2))',
                borderRadius: 'var(--hf-radius-md)',
                color: 'var(--hf-ink-3)',
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              <Icon name="x" size={16} />
            </button>
          </div>
        )}
        <div
          style={
            framed && !bare
              ? {
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  // Room for a field's focus ring inside the scroller's clip.
                  padding: 'var(--hf-space-1)',
                  margin: 'calc(-1 * var(--hf-space-1))',
                }
              : undefined
          }
        >
          {/* What a dialog holds sits on its surface: a Card in it is a flat
              panel, a KpiTile a bare figure (card.tsx, Surface). */}
          <Surface>
            <Fill slot={children} requestClose={requestClose} />
          </Surface>
        </div>
        {discarding ? (
          <div
            role="group"
            aria-labelledby={discardId}
            className="hf-dialog-actions"
            style={{
              ...actions(sheet),
              ...(bare ? { margin: 0, padding: 'var(--hf-space-3)' } : {}),
            }}
          >
            <span
              id={discardId}
              style={{ marginRight: 'auto', fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink)' }}
            >
              Discard changes?
            </span>
            <span ref={keepRef} style={{ display: 'contents' }}>
              <Button variant="ghost" onClick={keepEditing}>
                Keep editing
              </Button>
            </span>
            <Button variant="danger" onClick={onClose}>
              Discard
            </Button>
          </div>
        ) : (
          footer &&
          !bare && (
            <div className="hf-dialog-actions" style={actions(sheet)}>
              <Fill slot={footer} requestClose={requestClose} />
            </div>
          )
        )}
      </div>
    </dialog>
  );
}

// A slot's function is called here, in a component of its own, so requestClose
// arrives as a prop: it reads refs, and handing it to a function called during
// the Dialog's own render is what react-hooks/refs forbids.
function Fill({ slot, requestClose }: { slot?: Slot; requestClose: () => void }) {
  return typeof slot === 'function' ? slot(requestClose) : slot;
}

// The actions row. Outside the scrolling body, so a sheet's actions stay put.
const actions = (sheet: boolean): CSSProperties => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 'var(--hf-space-2)',
  marginTop: sheet ? 'var(--hf-space-4)' : 'var(--hf-space-5)',
  flexShrink: 0,
});

const FIELD_STYLE: CSSProperties = {
  width: '100%',
  padding: 'var(--hf-pad-field)',
  borderRadius: 'var(--hf-radius-md)',
  background: 'var(--hf-surface)',
  border: '1px solid var(--hf-rule-control)',
  color: 'var(--hf-ink)',
  fontSize: 'var(--hf-text-md)',
  fontFamily: 'inherit',
};
const LABEL_STYLE: CSSProperties = {
  display: 'block',
  marginBottom: 'var(--hf-space-1)',
  fontSize: 'var(--hf-text-sm)',
  color: 'var(--hf-ink-2)',
};

type ConfirmProps = {
  open: boolean;
  title: string;
  /** What will happen. An array renders as a list, one consequence per item. */
  consequence: ReactNode;
  /** A verb: "Lock June", "Erase data" — never "OK". */
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  /** A written reason, kept with the record; confirm waits for minLength. */
  requireReason?: { label: string; minLength: number };
  /** Only for the irreversible (Lock month, Erase): type this exact text. */
  typeToConfirm?: string;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
};

// The house revoke flow (compHoursTrail: reason, consequence, then a named
// confirm) as one primitive, replacing window.confirm/prompt at the gravest acts
// in the app, where the focused default was OK and one Enter confirmed
// (DS-06, XA-11).
export function ConfirmDialog(props: ConfirmProps) {
  return props.open ? <OpenConfirm {...props} /> : null;
}

function OpenConfirm({
  title,
  consequence,
  confirmLabel,
  tone = 'primary',
  requireReason,
  typeToConfirm,
  onConfirm,
  onCancel,
}: ConfirmProps) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const reasonId = useId();
  const hintId = useId();
  const typeId = useId();
  const cancelRef = useRef<HTMLSpanElement>(null);
  const confirmRef = useRef<HTMLSpanElement>(null);

  const reasonOk = !requireReason || reason.trim().length >= requireReason.minLength;
  const typedOk = !typeToConfirm || typed.trim() === typeToConfirm;
  const ready = reasonOk && typedOk;
  const confirm = () => {
    if (ready) onConfirm(requireReason ? reason.trim() : undefined);
  };

  return (
    <Dialog
      open
      title={title}
      size="sm"
      onClose={onCancel}
      dirty={reason.trim() !== ''}
      // Danger starts on the way out: Enter on open must not be the destructive
      // act. A plain confirm with nothing to fill starts on the confirm.
      initialFocus={
        tone === 'danger' ? cancelRef : requireReason || typeToConfirm ? undefined : confirmRef
      }
      // Cancel asks before throwing away a typed reason, as Escape does.
      footer={(requestClose) => (
        <>
          <span ref={cancelRef} style={{ display: 'contents' }}>
            <Button variant="ghost" onClick={requestClose}>
              Cancel
            </Button>
          </span>
          <span ref={confirmRef} style={{ display: 'contents' }}>
            <Button variant={tone} disabled={!ready} onClick={confirm}>
              {confirmLabel}
            </Button>
          </span>
        </>
      )}
    >
      <div
        style={{
          fontSize: 'var(--hf-text-md)',
          lineHeight: 'var(--hf-leading-body)',
          color: 'var(--hf-ink-2)',
        }}
      >
        {Array.isArray(consequence) ? (
          <ul style={{ margin: 0, paddingLeft: 'var(--hf-space-5)' }}>
            {consequence.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        ) : (
          consequence
        )}
      </div>
      {requireReason && (
        <div style={{ marginTop: 'var(--hf-space-4)' }}>
          <label htmlFor={reasonId} style={LABEL_STYLE}>
            {requireReason.label}
          </label>
          <textarea
            id={reasonId}
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-describedby={hintId}
            aria-required="true"
            style={{ ...FIELD_STYLE, resize: 'vertical' }}
          />
          <div
            id={hintId}
            style={{
              marginTop: 'var(--hf-space-1)',
              fontSize: 'var(--hf-text-xs)',
              color: 'var(--hf-ink-3)',
            }}
          >
            At least {requireReason.minLength} characters.
          </div>
        </div>
      )}
      {typeToConfirm && (
        <div style={{ marginTop: 'var(--hf-space-4)' }}>
          <label htmlFor={typeId} style={LABEL_STYLE}>
            Type <span style={{ fontFamily: 'var(--hf-font-mono)' }}>{typeToConfirm}</span> to
            confirm
          </label>
          <input
            id={typeId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirm();
            }}
            autoComplete="off"
            spellCheck={false}
            style={FIELD_STYLE}
          />
        </div>
      )}
    </Dialog>
  );
}

// ── usePopover ───────────────────────────────────────────────────────────────
// Shared disclosure behaviour for a panel anchored to a trigger. The panel is
// popover="manual" and must render as a DOM SIBLING of its trigger: that keeps
// it in the top layer AND inside any modal <dialog> around it (Select used to
// portal to <body> at z-index 9999, which showModal() would make inert).
// Escape closes and hands focus back to the trigger; focus moving to anything
// outside trigger and panel closes; so does a press outside both. Render the
// panel only while `open` — the ref shows it on mount.
// It lives beside Dialog because both are the overlay layer; a hook export only
// costs this file Fast Refresh, which reloads it whole instead.
// eslint-disable-next-line react-refresh/only-export-components
export function usePopover({
  align = 'left',
  maxH = 280,
  minWidth = 0,
}: {
  align?: 'left' | 'right';
  maxH?: number;
  minWidth?: number;
} = {}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();

  // Below the trigger, flipped above when there is less room below than the
  // panel needs and more above (Select's rule, kept). All four sides are set:
  // the UA gives [popover] `inset: 0; margin: auto`, which would centre it.
  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom;
    const flip = below < Math.min(maxH, 280) + 14 && r.top > below;
    // The room from the anchored edge to the far gutter (16px). A min-width
    // beats a max-width, so the bell's 340px panel, anchored right at 320px,
    // ran 28px off the left edge of the screen; both are capped by the room.
    const room = (align === 'right' ? r.right : window.innerWidth - r.left) - 16;
    const cap = room > 0 ? room : undefined;
    setPos({
      position: 'fixed',
      margin: 0,
      top: flip ? 'auto' : r.bottom + 6,
      bottom: flip ? window.innerHeight - r.top + 6 : 'auto',
      left: align === 'right' ? 'auto' : r.left,
      right: align === 'right' ? window.innerWidth - r.right : 'auto',
      minWidth: Math.min(Math.max(r.width, minWidth), cap ?? Infinity),
      maxWidth: cap ?? 'calc(100vw - 2 * var(--hf-space-4))',
      maxHeight: maxH,
    });
  }, [align, maxH, minWidth]);

  const show = useCallback(() => {
    place();
    setOpen(true);
  }, [place]);
  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const press = (e: globalThis.MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    // Follow the trigger when anything but the panel itself scrolls.
    const scroll = (e: Event) => {
      if (!panelRef.current?.contains(e.target as Node)) place();
    };
    document.addEventListener('mousedown', press);
    window.addEventListener('resize', place, true);
    window.addEventListener('scroll', scroll, true);
    return () => {
      document.removeEventListener('mousedown', press);
      window.removeEventListener('resize', place, true);
      window.removeEventListener('scroll', scroll, true);
    };
  }, [open, place]);

  const panelMount = useCallback((el: HTMLDivElement | null) => {
    panelRef.current = el;
    el?.showPopover?.();
  }, []);

  // Only a known destination closes: a click on a non-focusable part of the
  // panel blurs to nothing (relatedTarget null) and must not close it before
  // its own click lands. The outside press covers the mouse.
  const onBlur = (e: FocusEvent) => {
    const to = e.relatedTarget as Node | null;
    if (to && !triggerRef.current?.contains(to) && !panelRef.current?.contains(to)) close(false);
  };
  // Escape stops here (UI-CONVENTIONS §4): prevented so a modal <dialog>
  // around it gets no cancel, stopped so a window listener never sees it.
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || !open) return;
    e.preventDefault();
    e.stopPropagation();
    close();
  };

  return {
    open,
    show,
    close,
    triggerRef,
    panelRef,
    panelId,
    triggerProps: {
      ref: triggerRef,
      'aria-expanded': open,
      'aria-controls': open ? panelId : undefined,
      onClick: () => (open ? close() : show()),
      onBlur,
      onKeyDown,
    },
    panelProps: {
      ref: panelMount,
      id: panelId,
      popover: 'manual' as const,
      style: pos,
      onBlur,
      onKeyDown,
    },
  };
}
